import { useEffect, RefObject } from 'react';

interface UseModalAccessibilityOptions {
  isOpen: boolean;
  onClose: () => void;
  dialogRef: RefObject<HTMLElement>;
  triggerRef?: RefObject<HTMLElement>;
  focusSelector?: string;
}

export function useModalAccessibility({
  isOpen,
  onClose,
  dialogRef,
  triggerRef,
  focusSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
}: UseModalAccessibilityOptions) {
  
  // Handle ESC key to close dialog
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
        // Return focus to trigger element
        triggerRef?.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Focus the first focusable element when dialog opens
      const firstFocusableElement = dialogRef.current?.querySelector(focusSelector) as HTMLElement;
      if (firstFocusableElement) {
        setTimeout(() => firstFocusableElement.focus(), 0);
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, dialogRef, triggerRef, focusSelector]);

  // Handle focus trapping within the dialog
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    const dialog = dialogRef.current;
    const focusableElements = dialog.querySelectorAll(focusSelector);
    const firstFocusableElement = focusableElements[0] as HTMLElement;
    const lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          // Shift + Tab: move backwards
          if (document.activeElement === firstFocusableElement) {
            event.preventDefault();
            lastFocusableElement.focus();
          }
        } else {
          // Tab: move forwards
          if (document.activeElement === lastFocusableElement) {
            event.preventDefault();
            firstFocusableElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    
    return () => {
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [isOpen, dialogRef, focusSelector]);

  // Handle click outside to close dialog
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
        triggerRef?.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, dialogRef, triggerRef]);

  // Return a function to handle closing with focus return
  const handleClose = () => {
    onClose();
    triggerRef?.current?.focus();
  };

  return { handleClose };
} 